import axios from "../../utils/axios";
import { LocalStorageKey } from "../../constants/localStorageKeys";
import { ESttausCode } from "../../enums";
import { getErrorMsg, withServicePrefix } from "../../helpers/common";

/**
 * Download is a background job, not a file response.
 *
 * The two "download" endpoints no longer stream a zip: they hand back a job that
 * Dagster works on (zip is built and pushed to MinIO), and once it is `READY`
 * the job carries a presigned URL the browser fetches straight from storage.
 */
export type DownloadJobStatus = "PENDING" | "PROCESSING" | "READY" | "ERROR";

export interface DownloadJob
{
  job_id: string;
  status: DownloadJobStatus;
  /** Presigned MinIO URL — only on READY. Lives 90 minutes, single use. */
  download_url: string | null;
  /** When `download_url` expires — only on READY. */
  expires_at: string | null;
  /** Fallback route through the backend — only on READY. */
  proxy_url: string | null;
  /** Reason, usually on ERROR. */
  note: string | null;
}

const getBaseUrl = (): string =>
{
  try
  {
    const staticConfigs = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
    );
    return staticConfigs?.REACT_APP_API_BASE_URL ?? "";
  } catch
  {
    return "";
  }
};

/**
 * Job URL for downloading a whole order.
 *
 * Lives under `/presigned/*`, a set of endpoints added alongside the original
 * ones — the un-prefixed paths still stream a zip through the backend and must
 * not be polled.
 *
 * The endpoint is idempotent — calling it again returns the same job instead of
 * re-zipping — so the same URL doubles as the poll URL and as the "retry" call
 * that resets an ERROR job back to PENDING. (There is a dedicated `/status`
 * endpoint, but it fails instead of creating a job that does not exist yet.)
 */
export function getOrderDownloadJobUrl(orderId?: string | number): string
{
  return `${getBaseUrl()}${withServicePrefix(
    `/api/v1/downloads/presigned/order/${orderId}`,
  )}`;
}

/**
 * Job URL for downloading a single image (STAC item) of an order item.
 * Idempotent like the whole-order one above.
 */
export function getStacItemDownloadJobUrl(
  orderItemId: string | number,
  stacItemId: string,
): string
{
  return `${getBaseUrl()}${withServicePrefix(
    `/api/v1/downloads/presigned/order-item/${orderItemId}`,
  )}?stac_item_id=${encodeURIComponent(stacItemId)}`;
}

/**
 * Create-or-read the job behind `url`. Auth comes from the shared axios
 * interceptor; the response is the service's usual
 * `{ timestamp, status_code, message, data }` wrapper.
 *
 * Throws an Error carrying the backend's own message (already user-facing
 * Vietnamese) when the job cannot be read.
 */
export async function fetchDownloadJob(
  url: string,
  signal?: AbortSignal,
): Promise<DownloadJob>
{
  const response: any = await axios.get(url, { signal });

  // The shared response interceptor *resolves* non-401 failures with the axios
  // error instead of rejecting, so a 400 would otherwise pass for a success and
  // leave the caller polling an empty job forever.
  if (response?.status !== ESttausCode.OK)
  {
    // A cancelled request lands here too — pass it through untouched so the
    // caller can recognise the abort rather than report it as a failure.
    if (axios.isCancel(response)) throw response;
    throw new Error(getErrorMsg(response));
  }

  const job = response?.data?.data as DownloadJob;

  if (!job?.status)
  {
    throw new Error(response?.data?.message || "");
  }

  return job;
}
